/**
 * whenKakaoMapsReady — 카카오 지도 SDK 가 "생성자까지" 준비될 때를 기다린다.
 *
 * 배경 (2026-09-21 버그): index.html 은 /api/settings/ 로 키를 받은 뒤 SDK 를
 * `autoload=false` 로 동적 삽입한다. 그래서 상세 페이지에 직접 진입하면
 *   ① window.kakao 자체가 아직 없거나
 *   ② window.kakao.maps 는 있지만 kakao.maps.load() 완료 전이라 LatLng 이 없다
 * 두 경우가 생기고, ②에서 `new kakao.maps.LatLng` 을 부르면
 * "kakao.maps.LatLng is not a constructor" 로 ErrorBoundary 전체 오류 화면이 떴다.
 *
 * 동작: window.kakao.maps 가 생길 때까지 폴링 → LatLng 이 없으면 kakao.maps.load(cb) 로
 * 초기화 완료를 기다린 뒤 resolve. 제한 시간 안에 준비되지 않으면 null 로 resolve
 * (호출부는 지도만 생략하고 페이지는 정상 유지). 절대 throw 하지 않는다.
 */

const POLL_MS = 100;
const DEFAULT_TIMEOUT_MS = 15000;

export function isKakaoMapsReady(): boolean {
  const k = (window as any).kakao;
  return !!(k && k.maps && typeof k.maps.LatLng === 'function' && typeof k.maps.Map === 'function');
}

export function whenKakaoMapsReady(timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<any | null> {
  return new Promise((resolve) => {
    if (isKakaoMapsReady()) {
      resolve((window as any).kakao);
      return;
    }
    const started = Date.now();
    let loadRequested = false;

    const tick = () => {
      const k = (window as any).kakao;
      if (isKakaoMapsReady()) {
        resolve(k);
        return;
      }
      if (k && k.maps && typeof k.maps.load === 'function' && !loadRequested) {
        loadRequested = true;
        try {
          k.maps.load(() => {
            if (isKakaoMapsReady()) resolve((window as any).kakao);
          });
        } catch {
          /* load 호출 실패 시 폴링으로 계속 대기 */
        }
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(null);
        return;
      }
      setTimeout(tick, POLL_MS);
    };
    tick();
  });
}

export default whenKakaoMapsReady;
